import { Text } from '@timelyapp/react';
import React from 'react';
import type { LinkedWorkItem } from 'redux/modules/superreports-datatable.types';
import { COLUMN_SIZE_MEDIUM } from './constants';
import HeaderWithMenu from './HeaderWithMenu';
import type { DatatableCellRenderer, DatatableColumnGetter } from './types';

type LinkedWorkField = 'external_code' | 'title' | 'status' | 'jira_custom_field';

export const getLinkedWorkColumn: DatatableColumnGetter<
  LinkedWorkItem[] | undefined,
  { label: string; field: LinkedWorkField; index?: number; parentColumnId?: string }
> = (
  id: string,
  { label, field, index, parentColumnId } = { label: '', field: 'external_code' }
) => ({
  id,
  header: props => <HeaderWithMenu {...props} label={label} sortLabelType="string" />,
  minSize: COLUMN_SIZE_MEDIUM,
  enableResizing: true,
  enableSorting: false,
  meta: {
    numeric: false,
    mandatory: false,
    label: label,
    parentColumnId: parentColumnId,
    linkedWork: true,
    linkedWorkField: field,
    linkedWorkIndex: index,
    labelPlural:
      field === 'external_code'
        ? 'IDs'
        : field === 'title'
          ? 'Titles'
          : field === 'jira_custom_field'
            ? 'Jira Custom Fields'
            : 'Sync Statuses',
  },
  cell: props => <LinkedWorkCell {...props} field={field} index={index} />,
});

export const LinkedWorkCell: DatatableCellRenderer<
  LinkedWorkItem[] | undefined,
  { field: LinkedWorkField; index?: number }
> = ({ getValue, field, index }) => {
  const linkedWorkItems = getValue();

  if (!linkedWorkItems || linkedWorkItems.length === 0) {
    return null;
  }

  // If index is specified, show only that specific item
  if (index !== undefined) {
    const item = linkedWorkItems[index];

    if (!item) {
      return null;
    }

    // For external_code, show as clickable link if html_url exists
    if (field === 'external_code' && item.external_code) {
      return (
        <Text size="small" className="fs-mask">
          {item.html_url ? (
            <a
              href={item.html_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
              onClick={e => e.stopPropagation()}
            >
              {item.external_code}
            </a>
          ) : (
            item.external_code
          )}
        </Text>
      );
    }

    // For title and status, show the value
    const value = item[field] || '';
    return (
      <Text size="small" className="fs-mask">
        {value}
      </Text>
    );
  }

  // Legacy: If no index specified, show all items comma-separated
  // For external_code, show all codes as clickable links if html_url exists
  if (field === 'external_code') {
    const items = linkedWorkItems.filter(item => item.external_code);

    if (items.length === 0) {
      return null;
    }

    return (
      <Text size="small" className="fs-mask">
        {items.map((item, idx) => (
          <React.Fragment key={item.uri}>
            {idx > 0 && ', '}
            {item.html_url ? (
              <a
                href={item.html_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
                onClick={e => e.stopPropagation()}
              >
                {item.external_code}
              </a>
            ) : (
              item.external_code
            )}
          </React.Fragment>
        ))}
      </Text>
    );
  }

  // For title and status, show all items comma-separated
  const values = linkedWorkItems
    .map(item => item[field])
    .filter(value => value && value.trim() !== '');

  if (values.length === 0) {
    return null;
  }

  return (
    <Text size="small" className="fs-mask">
      {values.join(', ')}
    </Text>
  );
};
